import { useReferralCode } from "src/hooks/referrals";
import { getReferralLinkWithTracking } from "@utils/referrals/shareSourceTracking";
import i18n from "i18next";
import { trackBuyflowEvent, trackEvent } from "src/utils/api/tracker";
import { useUserData } from "./redux";
import { v4 as uuidv4 } from "uuid";
import { useCallbackRef } from "./lifecycle";
import { NoomBuyflowEvents } from "src/utils/monitoring/events";

export enum SharingScreen {
  FRIENDS_ONBOARDING = "friends_onboarding",
  FRIENDS_PROMO_ONBOARDING = "friends_promo_onboarding",
}

export interface MobileSharingInterfaceShareResult {
  shareId?: string;
  error?: string;
  targetComponent?: string;
  success?: boolean;
}

export interface ShareRequest {
  text: string;
  title?: string;
  screen?: SharingScreen;
}

/**
 * This injected into webView interface will allow us to use a native
 * share function both on Android and iOS
 */
interface MobileSharingInterface {
  /**
   *
   * @param request Request containing a text to share and an optional title. Those will be used by the native share api
   *                The request needs to be a json stringified instance of ShareRequest
   * @param id Optional token used to listen to async response from the device.
   */
  share(request: string, id?: string): void;
}

async function shareWithMobileSharingInterface(
  shareRequest: ShareRequest
): Promise<MobileSharingInterfaceShareResult> {
  const shareId = uuidv4();

  // Injected into Android web views
  const mobileSharingInterface = (window as any)
    .mobileSharingInterface as MobileSharingInterface;

  if (!mobileSharingInterface) {
    return Promise.resolve({
      shareId,
      success: false,
      error: "mobileSharingInterface injection is unavailable",
    });
  }

  return new Promise<MobileSharingInterfaceShareResult>((resolve) => {
    function shareResultHandler(ev: MessageEvent<string>) {
      const result = JSON.parse(ev.data) as MobileSharingInterfaceShareResult;
      if (result.shareId === shareId) {
        window.removeEventListener("message", shareResultHandler);
        resolve(result);
      }
    }

    window.addEventListener("message", shareResultHandler);
    mobileSharingInterface.share(JSON.stringify(shareRequest), shareId);
  });
}

function getNativeShareObject(referralCode: string): ShareRequest {
  const referralLink = getReferralLinkWithTracking(referralCode, "shareApi");
  const shareMessage = i18n.t("referral:share:smsMessage", { referralLink });

  return {
    text: shareMessage,
  };
}

export function canUseMobileSharingInterface(): boolean {
  return "mobileSharingInterface" in window;
}

async function callMobileSharingInterfaceShare(shareRequest: ShareRequest) {
  const shareResult = await shareWithMobileSharingInterface(shareRequest);

  if (shareResult.error) {
    // This is an actual error thrown from the mobile app that occurred during sharing
    trackEvent("OnFailedToUseShareApi", {
      errorMessage: shareResult.error,
    });
    return false;
  }
  if (!shareResult.success) {
    // This happens when the user does not choose a sharing surface
    // or aborts the operation after from within the chosen surface.
    trackEvent("OnShareAborted", {
      channel: shareResult.targetComponent,
    });
    return false;
  }
  trackEvent("OnSharedWithChannel", {
    channel: shareResult.targetComponent,
  });
  return true;
}

export function canUseNavigatorShare(): boolean {
  return (
    "canShare" in navigator &&
    navigator.canShare({ text: "anything", url: "anything" }) &&
    "share" in navigator
  );
}

async function shareWithNavigator(shareRequest: ShareRequest) {
  try {
    await navigator.share(shareRequest);
    return true;
  } catch (e) {
    trackEvent("OnFailedToUseShareApi", { errorMessage: e.message });
    return false;
  }
}

/**
 * @deprecated use useMobileSharingInterfaceApi instead.
 * As soon as MobileSharingInterface has been added to iOS as well as on Android,
 * we can use the same approach on both platforms.
 * @param referralEntryPoint
 */
export function useSharingApi(
  referralEntryPoint: NoomBuyflowEvents["referralSubmitted"]["referralEntryPoint"]
) {
  const { referralCode } = useReferralCode();
  const { user_id: userId, access_code: accessCode } = useUserData();

  const shareViaShareApi = async () => {
    const shareRequest = getNativeShareObject(referralCode);

    const sharingFunction =
      (canUseNavigatorShare() && shareWithNavigator) ||
      (canUseMobileSharingInterface() && callMobileSharingInterfaceShare);

    if (!sharingFunction) {
      trackEvent("OnShareApiUnavailable");
      return false;
    }

    const success = await sharingFunction(shareRequest);

    if (success) {
      trackBuyflowEvent("ReferralSubmitted", {
        referrerGrowthUserId: userId,
        referrerAccessCode: accessCode,
        referralMethod: "SHAREAPI",
        referralEntryPoint,
      });
    }

    return success;
  };

  return {
    shareViaShareApi: useCallbackRef(shareViaShareApi),
  };
}

export function useMobileSharingInterfaceApi() {
  const shareViaMobileSharingInterface = async (shareRequest: ShareRequest) => {
    const sharingFunction =
      (canUseMobileSharingInterface() && callMobileSharingInterfaceShare) ||
      // Backward compatibility with old iOS builds
      (canUseNavigatorShare() && shareWithNavigator);

    if (!sharingFunction) {
      trackEvent("OnShareApiUnavailable");
      return false;
    }

    return sharingFunction(shareRequest);
  };

  return {
    shareViaMobileSharingInterface: useCallbackRef(
      shareViaMobileSharingInterface
    ),
  };
}
